import { apiService } from "api/apiService";
import useHelpers from "composables/useHelpers";
import { defineStore } from "pinia";
import { computed, reactive, ref } from "vue";

export const useGlobalStore = defineStore("global", () => {
  // Helpers
  const { schoolId } = useHelpers();

  // Refs
  const fetchedSchoolModulesId = ref(null);
  const fetchedSchoolDataId = ref(null);

  // Reactive
  const globals = reactive({
    menuOpen: false,
    showConfetti: false,
    schoolSettings: null,
    unreadTasksCount: 0,
    unreadNotificationsCount: 0,
    showSettingsPage: true,
    schoolModules: [],
    school: {},
    statusMessage: {
      message: null,
      active: false,
    },
  });

  // Computed
  const hasFetchedSchoolData = computed(() => {
    return fetchedSchoolDataId.value === schoolId;
  });

  const hasFetchedModuleData = computed(() => {
    return fetchedSchoolModulesId.value === schoolId;
  });

  // Methods
  const getSchoolModules = async () => {
    try {
      const { data } = await apiService.get(`/api/school/${schoolId}/modules`);

      globals.schoolModules = data;

      fetchedSchoolModulesId.value = schoolId;
    } catch (error) {
      console.log("🚀 ~ getSchoolModules ~ error:", error);
    }
  };

  const getSchool = async () => {
    try {
      const { data } = await apiService.get(`/api/schools/${schoolId}`);

      globals.school = data.data;

      fetchedSchoolDataId.value = schoolId;
    } catch (error) {
      console.log("🚀 ~ getSchool ~ error:", error);
    }
  };

  const setStatusMessage = (message) => {
    globals.statusMessage = {
      message: message ?? null,
      active: Boolean(message),
    };
  };

  const getStatusMessage = async () => {
    try {
      const { version } = await import("~/package.json");

      const { data } = await apiService.get("/api/status-message", {
        headers: { "version-number": version },
      });

      setStatusMessage(data.messages);
    } catch (error) {
      console.log("🚀 ~ getStatusMessage ~ error:", error);

      setStatusMessage(null);
    }
  };

  // The editor reads the stored column, never the public endpoint: that one is
  // cached in `globals`, can synthesize an outdated-version warning, and hides
  // failures behind an empty message — all of which would let a save wipe the
  // live status message.
  const getWebmasterStatusMessage = async () => {
    const { data } = await apiService.get("/api/webmaster/system-message");

    return data.data.messages ?? "";
  };

  const saveStatusMessage = async (message) => {
    const { data } = await apiService.put("/api/webmaster/system-message", {
      messages: message,
    });

    // Show what was stored, not what was typed: the backend purifies the HTML.
    setStatusMessage(data.data.messages);
  };

  return {
    globals,
    getSchoolModules,
    hasFetchedSchoolData,
    hasFetchedModuleData,
    getSchool,
    getStatusMessage,
    getWebmasterStatusMessage,
    saveStatusMessage,
  };
});
